/* eslint-disable new-cap */
// eslint-disable-next-line angular/factory-name
angular.
module("dx.webapp.rubrics").
factory("RepositoryConstructor", function repositoryConstructorFactory(
	$http,
	$q,
	$timeout
) {
	"use strict";

	var debouncedSaves = {};

	function getItemCacheId(item) {
		return item.cacheId || item.id;
	}

	function prependTransform(transform, defaults) {
		if (!transform) {
			return defaults;
		}

		if (!Array.isArray(transform)) {
			// eslint-disable-next-line no-param-reassign
			transform = [transform];
		}
		return transform.concat(defaults);
	}

	function getSaveFunction(repo, data) {
		return function saveFn() {
			delete debouncedSaves[getItemCacheId(data)];
			return $http({
				method: "POST",
				url: repo.urls.save,
				data: data,
				transformRequest: prependTransform(repo.transformRequest, $http.defaults.transformRequest),
				transformResponse: prependTransform(repo.transformResponse, $http.defaults.transformResponse),
			}).then(repo.successCallback, repo.errorCallback);
		};
	}

	function RepositoryConstructor(extensions) {
		// extensions object:
		// 	itemConstructor:   Object/Constructor - Used to create new item instances
		// 	draftType:         String - The type expected by the Draft API
		// 	urls:
		// 		load:            String - Load a single item from the server
		// 		loadDefault:     String - Load the default items from the server
		// 		loadArchived:    String - Load the archived items from the server
		// 		save:            String - Save a single item to the server
		// 		loadDraft:       String - Load a single draft item from the server
		// 		saveDraft:       String - Save a draft item to the server
		// 	transformRequest:  Function or Array.<Function> - Prepended to the default request transformers, see Angular docs for $http
		// 	transformResponse: Function or Array.<Function> - Prepended to the default response transformers, see Angular docs for $http

		this.items = [];
		this.cacheLookup = {};
		// Used by templates to avoid rendering "no content" messages before
		// we know if there is any content.
		this.initiallyLoaded = false;
		angular.extend(this, extensions);

		this.cacheNewItem = this.cacheNewItem.bind(this);
		this.successCallback = this.successCallback.bind(this);
	}

	RepositoryConstructor.prototype.load = load;
	RepositoryConstructor.prototype.loadDraft = loadDraft;
	RepositoryConstructor.prototype.loadMany = loadMany;
	RepositoryConstructor.prototype.loadDefault = loadDefault;
	RepositoryConstructor.prototype.loadArchived = loadArchived;
	RepositoryConstructor.prototype.save = save;
	RepositoryConstructor.prototype.saveDraft = saveDraft;
	RepositoryConstructor.prototype.cache = cache;
	RepositoryConstructor.prototype.cacheNewItem = cacheNewItem;
	RepositoryConstructor.prototype.successCallback = successCallback;
	RepositoryConstructor.prototype.errorCallback = errorCallback;
	RepositoryConstructor.prototype.reset = reset;

	function load(id, useCache) {
		// You must disable the cache if you use angular.clone or
		// in some other way break the reference to the item in
		// the promise callback because the cache will resolve the
		// promise right away, before the reload is complete.
		if (!isFinite(id)) {
			throw new TypeError("[RepositoryConstructor.load()] `id` argument must be a finite number, it was: " + id);
		}

		// Reload the item from the server.
		var promise = $http({
			method: "GET",
			url: this.urls.load + id,
			transformResponse: prependTransform(this.transformResponse, $http.defaults.transformResponse),
		}).then(this.successCallback, this.errorCallback);

		if (typeof useCache === "undefined" || useCache) {
			// Use the currently cached item, it will be updated
			// once the reload is complete.
			//
			// Can't use getItemCacheId() here because we don't have the item...
			// Items using the item cache should use their `id` property,
			// getItemCacheId() is mostly used for debouncing saving items that
			// does not have an `id` property.
			var cachedItem = this.cacheLookup[id];
			if (cachedItem) {
				var deferred = $q.defer();
				deferred.resolve(cachedItem);
				promise = deferred.promise;
			}
		}

		return promise;
	}

	function loadDraft(id) {
		// eslint-disable-next-line consistent-this
		var that = this;

		if (!isFinite(id)) {
			throw new TypeError("[RepositoryConstructor.loadDraft()] `id` argument must be a finite number, it was: " + id);
		}

		return $http({
			method: "GET",
			url: this.urls.loadDraft + id,
			transformResponse: prependTransform(this.transformResponse, $http.defaults.transformResponse),
		}).then(function success(res) {
			var data = res.data;

			if (!(typeof data.data === "string" && data.data.length)) {
				return null;
			}
			var draft = JSON.parse(data.data);

			return new that.itemConstructor(draft);
		});
	}

	function loadMany(url) {
		if (!(typeof url === "string" && url.length)) {
			throw new TypeError("[RepositoryConstructor.loadMany()] `url` argument must be a string with length, it was: " + url);
		}

		return $http({
			method: "GET",
			url: url,
			transformResponse: prependTransform(this.transformResponse, $http.defaults.transformResponse),
		}).then(this.successCallback, this.errorCallback);
	}

	// Shorthands to common loadMany use-cases
	//
	// You can add more of these by passing a line just like this in the `extensions`
	// object when you extend the RepositoryConstructor.
	function loadDefault() {
		return this.loadMany(this.urls.loadDefault);
	}

	function loadArchived() {
		return this.loadMany(this.urls.loadArchived);
	}

	function save(item, debounceDurationArg) {
		var debounceDuration = debounceDurationArg ? debounceDurationArg : 0;

		if (!(angular.isObject(item) || typeof item === "string" || typeof item === "number" && isFinite(item))) {
			throw new TypeError("[RepositoryConstructor.save()] `item` argument must be an object, array, string or finite number, it was: " + item);
		}

		var cacheId = getItemCacheId(item);
		var debouncedSave = debouncedSaves[cacheId];
		if (debouncedSave) {
			$timeout.cancel(debouncedSave);
		}
		if (debounceDuration > 0) {
			debouncedSaves[cacheId] = $timeout(getSaveFunction(this, item), debounceDuration);
			return debouncedSaves[cacheId];
		}
		return getSaveFunction(this, item)();
	}

	// TODO(kenneth): maybe add support for debouncing draft saving too?
	function saveDraft(id, draftData) {
		// eslint-disable-next-line consistent-this
		var that = this;

		if (!(typeof id === "number" && isFinite(id))) {
			throw new TypeError("[RepositoryConstructor.saveDraft()] `id` argument must be a finite number, it was: " + id);
		}

		if (!(typeof draftData === "string")) {
			throw new TypeError("[RepositoryConstructor.saveDraft()] `draftData` argument must be a string, it was: " + draftData);
		}

		var data = {
			type: this.draftType,
			objectId: id,
			data: draftData,
		};

		return $http({
			method: "POST",
			url: this.urls.saveDraft,
			data: data,
			transformRequest: prependTransform(this.transformRequest, $http.defaults.transformRequest),
			transformResponse: prependTransform(this.transformResponse, $http.defaults.transformResponse),
		}).then(function success(response) {
			return new that.itemConstructor(response.data);
		});
	}

	function cache(item) {
		if (item && typeof item.id === "number" && item.id > 0) {
			var cachedItem = this.cacheLookup[item.id];
			if (cachedItem) {
				Object.keys(item).forEach(function forEachFn(key) {
					// Checks to prevent copy of properties that is the same
					if (typeof item[key] === "function") {
						return;
					}
					if (item[key] instanceof Array && item[key].compare(cachedItem[key])) {
						return;
					}
					if (cachedItem[key] === item[key]) {
						return;
					}
					cachedItem[key] = item[key];
				});
				// eslint-disable-next-line no-param-reassign
				item = cachedItem;
			} else {
				this.items.push(item);
				this.cacheLookup[item.id] = item;
			}
		}
		return item;
	}

	function cacheNewItem(data) {
		return this.cache(new this.itemConstructor(data));
	}

	function successCallback(res) {
		this.initiallyLoaded = true;

		if (res && res.data) {
			if (Array.isArray(res.data)) {
				return res.data.map(this.cacheNewItem);
			}

			return this.cacheNewItem(res.data);
		}
		return null;
	}

	function errorCallback(res) {
		// Transform the server error response data from {faults:[Ints], msgs:[Strings]} into just the msgs array
		if (res.status >= 500 && res.status < 600 && Array.isArray(res.data.msgs)) {
			res.data = res.data.msgs;
			// Wrap browser error messages in an array so we don't have to place checks in the next promises in the chain
		} else if (typeof res.data === "string") {
			res.data = [res.data];
		}
		if (!res.data) {
			res.data = [];
		}

		return $q.reject(res);
	}

	function reset() {
		this.items = [];
		this.cacheLookup = {};
		this.initiallyLoaded = false;
	}

	return RepositoryConstructor;
});
